/**
 * RescheduleDialog - Naver Place 스타일 예약 변경 Dialog
 * V226: UnifiedCalendar + TimeSlotPicker 통합 적용
 * V230: SCRUM-128 - 동적 영업시간/슬롯간격 조회 (AppointmentTimetablePage와 동일 로직)
 *
 * Features:
 * - 날짜 선택 (UnifiedCalendar)
 * - 시간 선택 (TimeSlotPicker)
 * - 변경 사유 입력 (선택)
 * - API 연동
 * - V230: 동적 영업시간, 슬롯 간격, 휴게시간 적용
 */

import { useState, useMemo, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { appointmentApi } from "@/api/appointment";
import { servicesApi } from "@/api/services";
import { bookingRulesApi } from "@/api/settings";
import { organizationsApi } from "@/api/organizations";
import { handleApiError } from "@/api/client";
import type { AppointmentTimeline } from "@/types/appointment";
import { useToast } from "@/components/ui/use-toast";
import dayjs from "dayjs";
import {
  Calendar as CalendarIcon,
  Clock,
  ArrowRight,
  AlertCircle,
  Package,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UnifiedCalendar, TimeSlotPicker } from "@/components/calendar";

/**
 * V230: 휴게시간 동안의 슬롯 목록 생성
 * @param breakStart 휴게 시작 시간 (예: "12:00")
 * @param breakEnd 휴게 종료 시간 (예: "13:00")
 * @param interval 슬롯 간격 (분)
 * @returns 휴게시간에 해당하는 슬롯 목록 (예: ["12:00", "12:30"])
 */
function generateBreakTimeSlots(
  breakStart: string,
  breakEnd: string,
  interval: number,
): string[] {
  const slots: string[] = [];
  const [startHour, startMin] = breakStart.split(":").map(Number);
  const [endHour, endMin] = breakEnd.split(":").map(Number);

  let currentHour = startHour;
  let currentMin = startMin;

  while (
    currentHour < endHour ||
    (currentHour === endHour && currentMin < endMin)
  ) {
    slots.push(
      `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`,
    );
    currentMin += interval;
    if (currentMin >= 60) {
      currentHour += Math.floor(currentMin / 60);
      currentMin = currentMin % 60;
    }
  }

  return slots;
}

interface RescheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentTimeline | null;
  onSuccess?: () => void;
}

export default function RescheduleDialog({
  isOpen,
  onClose,
  appointment,
  onSuccess,
}: RescheduleDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [reason, setReason] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [selectedSubItemId, setSelectedSubItemId] = useState<string>("");

  // V230: 동적 영업시간/슬롯 간격 상태
  const [businessHours, setBusinessHours] = useState<{
    start: string;
    end: string;
    breakStart?: string;
    breakEnd?: string;
  }>({
    start: "09:00",
    end: "18:00",
  });
  const [slotInterval, setSlotInterval] = useState(30);

  // V230: Organization 조회 (영업시간)
  const { data: organization } = useQuery({
    queryKey: ["organization"],
    queryFn: organizationsApi.getMyOrganization,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // V1069: booking_rules 테이블에서 슬롯 간격 로드
  const { data: bookingRule } = useQuery({
    queryKey: ["booking-rules"],
    queryFn: bookingRulesApi.get,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // 서비스 목록 조회 (상품 변경용)
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.getMyServices(),
    enabled: isOpen,
  });

  const visibleServices = useMemo(
    () => services.filter((s) => s.isVisible),
    [services],
  );
  const selectedService = visibleServices.find(
    (s) => s.id.toString() === selectedServiceId,
  );
  const subItems = selectedService?.subItems ?? [];

  useEffect(() => {
    if (
      bookingRule?.slotIntervalMinutes &&
      bookingRule.slotIntervalMinutes > 0
    ) {
      setSlotInterval(bookingRule.slotIntervalMinutes);
    }
  }, [bookingRule]);

  // V230: 영업시간 로드 (선택된 날짜의 요일에 맞게)
  useEffect(() => {
    if (organization && selectedDate) {
      const DAYS_OF_WEEK = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ] as const;
      const dayOfWeek = DAYS_OF_WEEK[
        selectedDate.getDay()
      ] as keyof typeof organization.businessHours;
      const dayHours = organization.businessHours?.[dayOfWeek];

      if (dayHours && !dayHours.closed) {
        setBusinessHours({
          start: dayHours.open || "09:00",
          end: dayHours.close || "18:00",
          breakStart: dayHours.breakStart,
          breakEnd: dayHours.breakEnd,
        });
      } else {
        // 휴무일이거나 영업시간 정보가 없는 경우 기본값
        setBusinessHours({
          start: "09:00",
          end: "18:00",
        });
      }
    }
  }, [organization, selectedDate]);

  // 초기값 설정 — useEffect로 side effect 처리 (useMemo에서 setState는 무한 루프 유발)
  useEffect(() => {
    if (appointment && isOpen) {
      // 현재 예약 날짜/시간을 기본값으로
      if (appointment.firstChoiceDate) {
        setSelectedDate(new Date(appointment.firstChoiceDate));
      }
      if (appointment.time) {
        setSelectedTime(appointment.time);
      }
      // 현재 서비스/서브아이템 초기값
      if (appointment.serviceId) {
        setSelectedServiceId(appointment.serviceId.toString());
      } else if (appointment.department && visibleServices.length > 0) {
        // serviceId가 없으면 department(진료 과목)으로 서비스 자동 매칭
        const dept = appointment.department.toLowerCase();
        const matched = visibleServices.find(
          (s) =>
            s.name.toLowerCase().includes(dept) ||
            dept.includes(s.name.toLowerCase()),
        );
        if (matched) {
          setSelectedServiceId(matched.id.toString());
        }
      }
      setSelectedSubItemId(
        appointment.subItemId ? appointment.subItemId.toString() : "",
      );
    }
  }, [appointment, isOpen, visibleServices]);

  // Mutation
  // V241: #6 버그 수정 - ReservationListPage 목록 갱신을 위해 consults-list, consults도 무효화
  const rescheduleMutation = useMutation({
    mutationFn: () => {
      if (!appointment || !selectedDate || !selectedTime) {
        throw new Error("필수 정보가 누락되었습니다.");
      }
      const newDate = dayjs(selectedDate).format("YYYY-MM-DD");
      const serviceId = selectedServiceId
        ? Number(selectedServiceId)
        : undefined;
      const subItemId = selectedSubItemId
        ? Number(selectedSubItemId)
        : undefined;
      return appointmentApi.reschedule(
        appointment.id,
        newDate,
        selectedTime,
        reason || undefined,
        serviceId,
        subItemId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment-summary"] });
      queryClient.invalidateQueries({ queryKey: ["appointment-history"] });
      queryClient.invalidateQueries({ queryKey: ["consults-list"] });
      queryClient.invalidateQueries({ queryKey: ["consults"] });
      const serviceName = selectedService?.name;
      const subItemName = subItems.find(
        (si) => si.id.toString() === selectedSubItemId,
      )?.name;
      const serviceDesc = serviceName
        ? ` / ${serviceName}${subItemName ? ` (${subItemName})` : ""}`
        : "";
      toast({
        title: "예약 변경 완료",
        description: `${dayjs(selectedDate).format("M월 D일")} ${selectedTime}${serviceDesc}으로 변경되었습니다.`,
      });
      onSuccess?.();
      handleClose();
    },
    onError: (error: unknown) => {
      toast({
        title: "예약 변경 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setSelectedDate(undefined);
    setSelectedTime("");
    setReason("");
    setSelectedServiceId("");
    setSelectedSubItemId("");
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedDate) {
      toast({
        title: "날짜 선택",
        description: "변경할 날짜를 선택해주세요.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedTime) {
      toast({
        title: "시간 선택",
        description: "변경할 시간을 선택해주세요.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedServiceId) {
      toast({
        title: "상품 선택",
        description: "상품을 선택해주세요.",
        variant: "destructive",
      });
      return;
    }
    rescheduleMutation.mutate();
  };

  // 변경 여부 확인
  const hasChanges = useMemo(() => {
    if (!appointment || !selectedDate || !selectedTime) return false;
    const originalDate = dayjs(appointment.firstChoiceDate).format(
      "YYYY-MM-DD",
    );
    const originalTime = appointment.time;
    const newDate = dayjs(selectedDate).format("YYYY-MM-DD");
    const originalServiceId = appointment.serviceId?.toString() ?? "";
    const originalSubItemId = appointment.subItemId?.toString() ?? "";
    return (
      newDate !== originalDate ||
      selectedTime !== originalTime ||
      selectedServiceId !== originalServiceId ||
      selectedSubItemId !== originalSubItemId
    );
  }, [
    appointment,
    selectedDate,
    selectedTime,
    selectedServiceId,
    selectedSubItemId,
  ]);

  if (!appointment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            예약 변경
          </DialogTitle>
          <DialogDescription>
            예약 일시를 변경합니다. 변경 후 고객에게 알림이 발송됩니다.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-6 space-y-6">
            {/* 현재 예약 정보 */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <Label className="text-sm text-muted-foreground">
                현재 예약 정보
              </Label>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-normal">
                  {appointment.customerName}
                </Badge>
                <Badge variant="secondary">{appointment.department}</Badge>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {dayjs(appointment.firstChoiceDate).format(
                    "YYYY년 M월 D일 (ddd)",
                  )}
                </span>
                <Clock className="h-4 w-4 text-muted-foreground ml-2" />
                <span className="font-medium">{appointment.time}</span>
              </div>
            </div>

            {/* 변경 화살표 */}
            <div className="flex justify-center">
              <div className="bg-primary/10 rounded-full p-2">
                <ArrowRight className="h-5 w-5 text-primary rotate-90" />
              </div>
            </div>

            {/* 날짜 선택 - V226: UnifiedCalendar 사용 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                변경할 날짜
              </Label>
              <UnifiedCalendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                minDate={new Date()}
                maxDate={
                  bookingRule?.maxAdvanceBookingDays
                    ? dayjs()
                        .add(bookingRule.maxAdvanceBookingDays, "day")
                        .toDate()
                    : undefined
                }
                compact
              />
            </div>

            {/* 시간 선택 - V230: 동적 영업시간/슬롯간격/휴게시간 적용 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                변경할 시간
                <span className="text-xs text-muted-foreground">
                  ({businessHours.start} ~ {businessHours.end})
                </span>
              </Label>
              <TimeSlotPicker
                selected={selectedTime}
                onSelect={setSelectedTime}
                startTime={businessHours.start}
                endTime={businessHours.end}
                interval={slotInterval}
                columns={4}
                closedSlots={
                  // V230: 휴게시간 슬롯 비활성화
                  businessHours.breakStart && businessHours.breakEnd
                    ? generateBreakTimeSlots(
                        businessHours.breakStart,
                        businessHours.breakEnd,
                        slotInterval,
                      )
                    : undefined
                }
              />
              {businessHours.breakStart && businessHours.breakEnd && (
                <p className="text-xs text-muted-foreground">
                  휴게시간: {businessHours.breakStart} ~{" "}
                  {businessHours.breakEnd}
                </p>
              )}
            </div>

            {/* 상품 변경 — 필수 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                상품 선택 <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedServiceId}
                onValueChange={(value) => {
                  setSelectedServiceId(value);
                  setSelectedSubItemId("");
                }}
              >
                <SelectTrigger
                  className={cn(
                    !selectedServiceId && "border-muted-foreground/30",
                  )}
                >
                  <SelectValue placeholder="상품을 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {visibleServices.map((service) => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {subItems.length > 0 && (
                <Select
                  value={selectedSubItemId}
                  onValueChange={setSelectedSubItemId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="세부 시술 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {subItems.map((si) => (
                      <SelectItem key={si.id} value={si.id.toString()}>
                        {si.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* 변경 사유 */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">변경 사유 (선택)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="예: 고객 요청으로 시간 변경"
                className="min-h-[80px] resize-none"
              />
            </div>

            {/* 변경 후 정보 미리보기 */}
            {hasChanges && selectedDate && selectedTime && (
              <div className="bg-success/10 border border-success/30 rounded-lg p-4 space-y-2">
                <Label className="text-sm text-success font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  변경 후 예약 정보
                </Label>
                <div className="flex items-center gap-2 text-sm text-success">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="font-medium">
                    {dayjs(selectedDate).format("YYYY년 M월 D일 (ddd)")}
                  </span>
                  <Clock className="h-4 w-4 ml-2" />
                  <span className="font-medium">{selectedTime}</span>
                </div>
                {selectedService && (
                  <div className="flex items-center gap-2 text-sm text-success">
                    <Package className="h-4 w-4" />
                    <span className="font-medium">
                      {selectedService.name}
                      {subItems.find(
                        (si) => si.id.toString() === selectedSubItemId,
                      )
                        ? ` (${subItems.find((si) => si.id.toString() === selectedSubItemId)?.name})`
                        : ""}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="p-4 border-t bg-muted/30">
          <div className="flex gap-2 w-full">
            <Button variant="outline" className="flex-1" onClick={handleClose}>
              취소
            </Button>
            <Button
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={handleSubmit}
              disabled={!hasChanges || rescheduleMutation.isPending}
            >
              {rescheduleMutation.isPending ? "변경 중..." : "예약 변경"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
